/**
 * Admin interface for managing persona profiles and templates
 */

import { useState } from 'react';
import { usePersonaProfiles } from '@/hooks/usePersonaCatalog';
import { usePersonaTemplates } from '@/hooks/usePersonaTemplates';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Settings, Users, LayoutTemplate, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export default function AdminPersonas() {
  const [isSeedingData, setIsSeedingData] = useState(false);
  const { data: profiles, isLoading: profilesLoading, refetch: refetchProfiles } = usePersonaProfiles();
  const { data: templates, isLoading: templatesLoading, refetch: refetchTemplates } = usePersonaTemplates();

  const handleSeedData = async () => {
    setIsSeedingData(true);
    try {
      const { data, error } = await supabase.functions.invoke('seed-personas');
      
      if (error) throw error;
      
      toast.success('Data seeded successfully');
      refetchProfiles();
      refetchTemplates();
    } catch (error: any) {
      console.error('Seed error:', error);
      toast.error(`Failed to seed data: ${error.message}`);
    } finally {
      setIsSeedingData(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Persona Administration</h1>
          </div>
          <p className="text-muted-foreground">
            Manage persona profiles and templates for the wizard
          </p>
        </div>

        {/* Quick Actions */}
        <Card className="p-6 mb-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold mb-4 text-blue-900 dark:text-blue-100">Quick Actions</h3>
          <div className="flex gap-3">
            <Button
              onClick={handleSeedData}
              disabled={isSeedingData}
              variant="default"
            >
              {isSeedingData && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Seed Initial Data
            </Button>
            <Button variant="outline" onClick={() => {
              refetchProfiles();
              refetchTemplates();
              toast.success('Data refreshed');
            }}>
              Refresh Data
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            Use "Seed Initial Data" to populate the database with default personas and templates.
            This is safe to run multiple times (it will update existing records).
          </p>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultValue="profiles" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profiles">
              <Users className="h-4 w-4 mr-2" />
              Persona Profiles ({profiles?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="templates">
              <LayoutTemplate className="h-4 w-4 mr-2" />
              Templates ({templates?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Persona Profiles Tab */}
          <TabsContent value="profiles" className="space-y-4">
            {profilesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : profiles && profiles.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {profiles.map((profile) => (
                  <Card key={profile.id} className="p-4">
                    <h4 className="font-semibold mb-2">{profile.name}</h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      {profile.description}
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Input tokens:</span>
                        <span className="font-medium">{profile.input_mean.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Output tokens:</span>
                        <span className="font-medium">{profile.output_mean.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Docs queried:</span>
                        <span className="font-medium">{profile.typical_docs_queried}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Chunks:</span>
                        <span className="font-medium">{profile.chunks_pulled}</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Persona Profiles</h3>
                <p className="text-muted-foreground mb-4">
                  Click "Seed Initial Data" to populate the database.
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : templates && templates.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {templates.map((template) => (
                  <Card key={template.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-lg mb-1">{template.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {template.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Recommended for:</span>
                        </div>
                        <p className="text-xs">{template.recommended_for}</p>
                        <div className="flex justify-between mt-2">
                          <span className="text-muted-foreground">Total users:</span>
                          <span className="font-medium">{template.total_users.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h5 className="text-sm font-semibold">Personas ({template.items?.length || 0}):</h5>
                      {template.items?.map((item) => (
                        <div key={item.id} className="flex justify-between items-center text-sm py-1">
                          <span className="text-muted-foreground">{item.persona_profile_id}</span>
                          <div className="flex gap-4 text-xs">
                            <span className="font-medium">{item.percentage}%</span>
                            <span className="text-muted-foreground">{item.sessions_per_user_per_day} sessions/day</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Templates</h3>
                <p className="text-muted-foreground mb-4">
                  Click "Seed Initial Data" to populate the database.
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
